import { formatCurrency } from '@if/core/utils'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Alert, Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material'
import { useComputeFlinksIncome } from '@src/data/api/income-calculator/income-calculator-api'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'PE', 'QC', 'SK']
const PAY_FREQUENCIES = ['Weekly', 'BiWeekly', 'Monthly'] as const
const NET_AMOUNT_FIELDS = ['netAmount0', 'netAmount1', 'netAmount2', 'netAmount3'] as const

interface FlinksFormValues {
  netAmount0: string
  netAmount1: string
  netAmount2: string
  netAmount3: string
  payFrequency: string
  provinceIso: string
}

const FlinksCalculatorTab = () => {
  const { t } = useTranslation()
  const [computeFlinksIncome, isPending, data, isError, error] = useComputeFlinksIncome()
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FlinksFormValues>({
    mode: 'onChange',
    defaultValues: { netAmount0: '', netAmount1: '', netAmount2: '', netAmount3: '', payFrequency: '', provinceIso: '' },
  })

  const handleCalculate = handleSubmit(async (values) => {
    const amounts = NET_AMOUNT_FIELDS.map((f) => parseFloat(values[f])).filter((v) => !isNaN(v) && v > 0)
    const avg = amounts.reduce((sum, v) => sum + v, 0) / amounts.length
    await computeFlinksIncome({ netAmountPerPeriod: avg, payFrequency: values.payFrequency, provinceIso: values.provinceIso }).catch(() => undefined)
  })

  const handleCopyToClipboard = async () => {
    if (!data) return
    await navigator.clipboard.writeText(String(data.grossAnnualIncome))
  }

  const getAmountLabel = (index: number) => {
    const base = `${t('incomeCalculator.netAmountPerPeriod')} ${index + 1}`
    return index === 0 ? base : `${base} (${t('incomeCalculator.optional')})`
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      {NET_AMOUNT_FIELDS.map((name, i) => (
        <Controller
          key={name}
          name={name}
          control={control}
          rules={i === 0 ? { validate: (v) => parseFloat(v) > 0 } : { validate: (v) => !v || parseFloat(v) > 0 }}
          render={({ field }) => (
            <TextField
              {...field}
              label={getAmountLabel(i)}
              type="number"
              size="small"
              slotProps={{ htmlInput: { min: 0 } }}
            />
          )}
        />
      ))}
      <Controller
        name="payFrequency"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControl size="small">
            <InputLabel>{t('incomeCalculator.payFrequency')}</InputLabel>
            <Select {...field} label={t('incomeCalculator.payFrequency')}>
              {PAY_FREQUENCIES.map((f) => (
                <MenuItem key={f} value={f}>
                  {f === 'Weekly'
                    ? t('incomeCalculator.weekly')
                    : f === 'BiWeekly'
                      ? t('incomeCalculator.biWeekly')
                      : t('incomeCalculator.monthly')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      <Controller
        name="provinceIso"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControl size="small">
            <InputLabel>{t('incomeCalculator.province')}</InputLabel>
            <Select {...field} label={t('incomeCalculator.province')}>
              {PROVINCES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      <Button variant="contained" onClick={() => void handleCalculate()} disabled={!isValid || isPending}>
        {t('incomeCalculator.calculate')}
      </Button>
      {isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error instanceof Error ? error.message : String(error)}
        </Alert>
      )}
      {data && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('incomeCalculator.grossAnnualIncome')}: <strong>{formatCurrency(data.grossAnnualIncome)}</strong>
          </Typography>
          <Tooltip title={t('incomeCalculator.copyToClipboard')}>
            <IconButton size="small" onClick={() => void handleCopyToClipboard()}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  )
}

export default FlinksCalculatorTab
